import { useReactFlow } from '@xyflow/react';
import { useCallback } from 'react';

import { type AppNode } from '../types';
import { FIT_VIEW_PROGRAMMATIC_MIN_ZOOM } from '../utils/computeFitViewport';
import { getEffectiveViewportRect } from '../utils/getEffectiveViewportRect';
import { getFitViewPadding } from '../utils/getFitViewPadding';
import { toNodeRect } from '../utils/layout/nodeLayout';
import { computeVisibleFraction, getBoundsFromRects } from '../utils/viewportVisibility';
import { usePrefersReducedMotionRef, ZOOM_TO_FIT_ANIMATION_DURATION_MS } from './usePrefersReducedMotionRef';

/**
 * Fraction (0–1) of the target nodes' bounding area that must already be
 * visible in the current viewport to skip the zoom-to-fit operation.
 */
const VIEWPORT_VISIBILITY_THRESHOLD = 0.8;

export type ZoomToFitIfNeededParams = {
  nodes: AppNode[];
  withAnimation?: boolean;
};

export const useZoomToFitIfNeeded = () => {
  const { fitView, getViewport, getZoom } = useReactFlow();
  const prefersReducedMotionRef = usePrefersReducedMotionRef();

  const zoomToFitIfNeeded = useCallback(
    (params: ZoomToFitIfNeededParams) => {
      const { nodes, withAnimation = false } = params;

      if (nodes.length === 0) {
        return;
      }

      const nodeRects = nodes.map(toNodeRect);
      const nodeBounds = getBoundsFromRects(nodeRects);

      const viewportRect = getEffectiveViewportRect(getViewport());
      const visibleFraction = viewportRect ? computeVisibleFraction(nodeBounds, viewportRect) : null;

      if (viewportRect && visibleFraction !== null && visibleFraction >= VIEWPORT_VISIBILITY_THRESHOLD) {
        return;
      }

      const duration = withAnimation && !prefersReducedMotionRef.current ? ZOOM_TO_FIT_ANIMATION_DURATION_MS : 0;
      const nodeBoundsFitInViewport =
        viewportRect && nodeBounds.width <= viewportRect.width && nodeBounds.height <= viewportRect.height;

      // When nodes fit at current zoom, cap maxZoom to prevent zoom-in (pan only).
      // When they don't fit, allow fitView to zoom out as needed.
      const maxZoom = nodeBoundsFitInViewport ? getZoom() : Math.max(getZoom(), 1);

      requestAnimationFrame(() => {
        fitView({
          duration,
          maxZoom,
          minZoom: FIT_VIEW_PROGRAMMATIC_MIN_ZOOM,
          nodes: nodes.map((node) => ({ id: node.id })),
          padding: getFitViewPadding(),
        });
      });
    },
    [fitView, getViewport, getZoom, prefersReducedMotionRef],
  );

  return { zoomToFitIfNeeded };
};
